// package.json sits outside src/, so no alias can reach it.
// eslint-disable-next-line no-restricted-imports
import { name, version } from '../../package.json';

// Source maps resolve through the debug IDs `sentry-cli sourcemaps inject`
// writes; the release only tells Sentry which version an event came from.
export const SENTRY_RELEASE = `${name}@${version}`;

/**
 * Decide whether Sentry should initialize, and under which environment.
 *
 * History: init used to be gated on `import.meta.env.VITE_NODE_ENV === 'production'`,
 * but `VITE_NODE_ENV` is set in no Netlify context, so in the built bundle it was
 * `undefined` and Sentry never initialized — all error capture was a silent no-op
 * in production. We gate instead on Vite's guaranteed `import.meta.env.PROD`
 * (true for any `vite build`, false only on the local dev server), so a missing
 * custom env var can never silently disable monitoring again. See #476.
 *
 * The Sentry `environment` tag comes from `VITE_FIREBASE_ENV`, which IS set
 * per-context in netlify.toml (`prod` on production, `dev` on the dev branch),
 * so production and sandbox report as separate environments.
 *
 * @param {{PROD?: boolean, VITE_FIREBASE_ENV?: string}} [env] - typically import.meta.env
 * @returns {{enabled: boolean, environment: string}}
 */
export const resolveSentryConfig = (env = {}) => {
  const enabled = Boolean(env.PROD);

  let environment;
  if (env.VITE_FIREBASE_ENV === 'prod') {
    environment = 'production';
  } else if (env.VITE_FIREBASE_ENV === 'dev') {
    environment = 'development';
  } else {
    // Deploy previews / branch deploys don't set VITE_FIREBASE_ENV.
    environment = 'preview';
  }

  return { enabled, environment };
};

/**
 * URL patterns for errors that never originate from this site's own code.
 * `iabjs://` is Instagram's in-app-browser Android WebView internals
 * (navigation-performance-logger, postMessage bridge) — injected into any
 * page opened from Instagram/Facebook, unrelated to anything this app does.
 * See #519.
 */
export const SENTRY_DENY_URLS = [/^iabjs:\/\//];

/**
 * Message patterns for known non-actionable noise. Kept separate from
 * denyUrls because the offending stack frame isn't always on an `iabjs://`
 * URL for every variant of this error.
 */
export const SENTRY_IGNORE_ERRORS = [
  /Error invoking postMessage: Java exception was raised during method invocation/,
  // WebKit's IndexedDB error when an open request races a database deletion
  // (Safari tracking-prevention purge, "clear website data", a WKWebView host
  // app evicting storage). It escapes unhandled from @firebase/auth's
  // persistence poller (firebase-js-sdk#10382), affects only admin sign-in
  // state, and is not actionable here. See #555.
  /Database deleted by request of the user/,
  // Two more WebKit IndexedDB failures escaping the same Firebase Auth
  // persistence poller as above (#632).
  /Error looking up record in object store/,
  /Connection to Indexed Database server lost/,
  // A browser extension (Safari, DuckDuckGo) messaging a tab that has gone.
  // Nothing on this site calls runtime.sendMessage (#632).
  /Invalid call to runtime\.sendMessage/,
];

/**
 * What a browser says when the connection drops mid-request: Safari "Load
 * failed", Chrome "Failed to fetch", Firefox "NetworkError…", and iOS's
 * connection-lost and offline messages.
 */
const NETWORK_FAILURE_PATTERNS = [
  /^Load failed$/,
  /^Failed to fetch$/,
  /NetworkError when attempting to fetch resource/,
  /The network connection was lost/,
  /The Internet connection appears to be offline/,
];

const isNetworkFailureMessage = (message) =>
  typeof message === 'string' &&
  NETWORK_FAILURE_PATTERNS.some((pattern) => pattern.test(message));

/**
 * Whether an error is the customer's browser losing its connection (#632).
 * @param {Error} [error]
 * @returns {boolean}
 */
export const isNetworkFailure = (error) =>
  isNetworkFailureMessage(error?.message);

/**
 * Sentry `beforeSend`: keep a lost-connection event, but at warning level.
 * Network failures are worth seeing (a Clover or Firebase outage would look
 * like one) but a single phone dropping off Wi-Fi must not page anyone. The
 * exception chain is checked so a wrapping error ("Failed to fetch user")
 * whose cause is the network failure is downgraded too (#632).
 * @param {import('@sentry/react').ErrorEvent} event
 * @returns {import('@sentry/react').ErrorEvent}
 */
export const downgradeNetworkFailures = (event) => {
  const values = event?.exception?.values || [];
  if (!values.some((value) => isNetworkFailureMessage(value?.value))) {
    return event;
  }
  return {
    ...event,
    level: 'warning',
    tags: { ...(event.tags || {}), network_failure: 'true' },
  };
};
